/**
 * File Details Dialog
 * Shows detailed file information
 */

import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Stack,
  IconButton,
  Link,
  Alert,
  Divider,
} from '@mui/material'
import {
  Close,
  Download,
  ContentCopy,
  Star,
  StarBorder,
  OpenInNew,
  Image as ImageIcon,
  VideoFile,
  AudioFile,
  Description,
  InsertDriveFile,
  Person,
  Folder,
} from '@mui/icons-material'
import {
  formatFileSize,
  getFileTypeIcon,
  getCategoryName,
  FileCategory,
} from '@api/types/files'
import type { UserFileDto } from '@api/types/files'
import { useToggleFavorite } from '../hooks/useFiles'

interface FileDetailsDialogProps {
  open: boolean
  onClose: () => void
  file: UserFileDto | null
  onDelete?: () => void
}

function FilePreview({ file }: { file: UserFileDto }) {
  const isImage = file.mimeType.startsWith('image/')
  const isVideo = file.mimeType.startsWith('video/')
  const isAudio = file.mimeType.startsWith('audio/')

  if (isImage) {
    return (
      <Box
        component="img"
        src={file.fileUrl}
        alt={file.displayName || file.fileName}
        sx={{
          maxWidth: '100%',
          maxHeight: 300,
          objectFit: 'contain',
          borderRadius: 1,
        }}
      />
    )
  }

  if (isVideo) {
    return (
      <Box
        component="video"
        controls
        src={file.fileUrl}
        sx={{ maxWidth: '100%', maxHeight: 300, borderRadius: 1 }}
      />
    )
  }

  if (isAudio) {
    return <Box component="audio" controls src={file.fileUrl} sx={{ width: '100%' }} />
  }

  // Generic file icon
  const type = getFileTypeIcon(file.mimeType)
  const iconProps = { sx: { fontSize: 80, color: 'text.secondary' } }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 4,
        bgcolor: 'grey.100',
        borderRadius: 1,
      }}
    >
      {type === 'video' && <VideoFile {...iconProps} />}
      {type === 'audio' && <AudioFile {...iconProps} />}
      {type === 'document' && <Description {...iconProps} />}
      {type === 'image' && <ImageIcon {...iconProps} />}
      {type === 'file' && <InsertDriveFile {...iconProps} />}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {file.mimeType}
      </Typography>
    </Box>
  )
}

export function FileDetailsDialog({
  open,
  onClose,
  file,
  onDelete,
}: FileDetailsDialogProps) {
  const [copied, setCopied] = useState(false)
  const toggleFavorite = useToggleFavorite()

  if (!file) return null

  const handleCopyUrl = async () => {
    await navigator.clipboard.writeText(file.fileUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleToggleFavorite = () => {
    toggleFavorite.mutate(file.id)
  }

  const displayName = file.displayName || file.fileName

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { maxHeight: '90vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        File Details
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={handleToggleFavorite} size="small">
          {file.isFavorite ? (
            <Star sx={{ color: 'warning.main' }} />
          ) : (
            <StarBorder />
          )}
        </IconButton>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* File preview */}
          <FilePreview file={file} />

          {/* File URL */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              File URL
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                fullWidth
                size="small"
                value={file.fileUrl}
                InputProps={{ readOnly: true }}
              />
              <IconButton onClick={handleCopyUrl} color={copied ? 'success' : 'default'}>
                <ContentCopy />
              </IconButton>
              <IconButton component={Link} href={file.fileUrl} target="_blank">
                <OpenInNew />
              </IconButton>
            </Box>
            {copied && (
              <Typography variant="caption" color="success.main">
                URL copied to clipboard!
              </Typography>
            )}
          </Box>

          <Divider />

          {/* File metadata */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              File Information
            </Typography>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Filename
                </Typography>
                <Typography variant="body2">{file.fileName}</Typography>
              </Box>
              {file.displayName && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Display Name
                  </Typography>
                  <Typography variant="body2">{file.displayName}</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  File type
                </Typography>
                <Typography variant="body2">{file.mimeType}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  File size
                </Typography>
                <Typography variant="body2">{formatFileSize(file.fileSize)}</Typography>
              </Box>
              {file.width && file.height && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Dimensions
                  </Typography>
                  <Typography variant="body2">{file.width} x {file.height}</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Category
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {file.category === FileCategory.Personal ? (
                    <Person fontSize="small" color="primary" />
                  ) : (
                    <Folder fontSize="small" color="secondary" />
                  )}
                  <Typography variant="body2">{getCategoryName(file.category)}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body2">
                  {new Date(file.createdDate).toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Usage Count
                </Typography>
                <Typography variant="body2">{file.usageCount}</Typography>
              </Box>
              {file.folderName && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Folder
                  </Typography>
                  <Typography variant="body2">{file.folderName}</Typography>
                </Box>
              )}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          startIcon={<Download />}
          component={Link}
          href={file.fileUrl}
          download={file.fileName}
        >
          Download
        </Button>
        {onDelete && (
          <Button color="error" onClick={onDelete}>
            Delete
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
